import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button,
  IconButton,
  Grid,
  Alert
} from '@mui/material';
import { Add, Remove, Delete } from '@mui/icons-material';
import { useCart, useCartItems, useCartTotal } from '../../context/CartContext';
import { getProductById, getSiteSettings } from '../../services/FirestoreService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const Cart: React.FC = () => {
  const { state, dispatch } = useCart();
  const items = useCartItems();
  const total = useCartTotal();
  const [siteSettings, setSiteSettings] = useState<any>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const settings = await getSiteSettings();
        setSiteSettings(settings);
      } catch (error) {
        console.error('Error fetching site settings:', error);
        // Use default value if there's an error
        setSiteSettings({ freeShippingThreshold: 750 });
      }
    };

    fetchSiteSettings();
  }, []);

  const updateQuantity = async (id: string, change: number) => {
    const item = items.find(item => item.id === id);
    if (item) {
      const newQuantity = item.quantity + change;
      
      if (newQuantity <= 0) {
        dispatch({ type: 'REMOVE_ITEM', payload: id });
        return;
      }
      
      // Fetch the current product to check stock
      try {
        const product = await getProductById(id);
        if (product) {
          if (newQuantity > product.stock) {
            setErrorMessage(`Cannot increase quantity. Only ${product.stock} items available in stock.`);
            setTimeout(() => setErrorMessage(null), 3000);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching product to check stock:', error);
        setErrorMessage('Error checking product stock.');
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }
      
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity: newQuantity } });
    }
  };

  const removeItem = (id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Shopping Cart
        </Typography>

        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

        {items.length === 0 ? (
          <Typography variant="h6" sx={{ textAlign: 'center', py: 4 }}>
            Your cart is empty
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {item.image && (
                            <img 
                              src={item.image} 
                              alt={item.name} 
                              style={{ width: 60, height: 60, marginRight: 10, objectFit: 'cover' }}
                            />
                          )}
                          <div>
                            <Typography variant="subtitle1">{item.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {item.id}
                            </Typography>
                          </div>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        ₺{(item.discountedPrice ?? item.price).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconButton 
                            size="small" 
                            onClick={async () => await updateQuantity(item.id, -1)}
                          >
                            <Remove />
                          </IconButton>
                          <Typography sx={{ mx: 2 }}>{item.quantity}</Typography>
                          <IconButton 
                            size="small" 
                            onClick={async () => await updateQuantity(item.id, 1)}
                          >
                            <Add />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        ₺{((item.discountedPrice ?? item.price) * item.quantity).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="error" 
                          onClick={() => removeItem(item.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              <Box sx={{ flex: '1 1 48%', minWidth: '300px' }}>
                <Button 
                  variant="outlined" 
                  color="error"
                  onClick={clearCart}
                  sx={{ mr: 2 }}
                >
                  Clear Cart
                </Button>
                <Button 
                  variant="outlined" 
                  href="/"
                >
                  Continue Shopping
                </Button>
              </Box>
              <Box sx={{ flex: '1 1 48%', minWidth: '300px', textAlign: 'right' }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Total: ₺{total.toFixed(2)}
                  </Typography>
                  {siteSettings && total < siteSettings.freeShippingThreshold && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Add ₺{(siteSettings.freeShippingThreshold - total).toFixed(2)} more for free shipping!
                    </Typography>
                  )}
                  <Button 
                    variant="contained" 
                    color="success"
                    size="large"
                    href="/checkout"
                    onClick={async (e) => {
                      // Check stock availability for all items before proceeding
                      for (const item of items) {
                        const product = await getProductById(item.id);
                        if (product && item.quantity > product.stock) {
                          e.preventDefault();
                          setErrorMessage(`Item "${item.name}" only has ${product.stock} units in stock, but you have ${item.quantity} in your cart.`);
                          setTimeout(() => setErrorMessage(null), 5000);
                          return;
                        }
                      }
                    }}
                  >
                    Proceed to Checkout
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default Cart;